/**
 * useAgendaWhatsAppSend.js
 *
 * Envio dos avisos da agenda (agendamento criado, reagendado, oferta de
 * horário) pelo sistema, na hora, em vez de abrir o WhatsApp Web.
 *
 * - `useCanSendAgendaWhatsApp()`: lê `capabilities.agenda_send` do status do
 *   remetente (só com o número próprio conectado; desde 2026-10-23 nada ao
 *   paciente sai pelo número da Gabriela). Fail-closed: enquanto o status não carregou vale `false` e a
 *   tela segue abrindo o WhatsApp Web no clique.
 * - `useAgendaWhatsAppSend()`: usado pela página da agenda; devolve
 *   `notifyPatient(payload, onResult?)` (appointments/notify_patient_whatsapp.php) e o
 *   Toast com o resultado, que fica na página porque os drawers fecham ao
 *   salvar.
 */

import React, { useState, useCallback } from 'react';
import { DataProviderInstance } from '../../../../api/DataProvider';
import { useWhatsAppSender } from '../../../../contexts/WhatsAppSenderContext';
import Toast from '../../../../components/support/Toast';

const SUCCESS_MESSAGES = {
  created: {
    sent: 'Confirmação enviada ao paciente pelo WhatsApp.',
    queued: 'Confirmação entrou na fila de envio do WhatsApp.',
  },
  rescheduled: {
    sent: 'Aviso de reagendamento enviado ao paciente pelo WhatsApp.',
    queued: 'Aviso de reagendamento entrou na fila de envio do WhatsApp.',
  },
  slot_offer: {
    sent: 'Oferta de horário enviada ao paciente pelo WhatsApp.',
    queued: 'Oferta de horário entrou na fila de envio do WhatsApp.',
  },
  canceled: {
    sent: 'Aviso de cancelamento enviado pelo WhatsApp.',
    queued: 'Aviso de cancelamento entrou na fila de envio do WhatsApp.',
  },
};

const FAILURE_MESSAGE = 'Não foi possível enviar a mensagem pelo WhatsApp.';

export const useCanSendAgendaWhatsApp = () => {
  const { capabilities } = useWhatsAppSender();
  return capabilities?.agenda_send === true;
};

export function useAgendaWhatsAppSend() {
  const canSendViaSystem = useCanSendAgendaWhatsApp();
  const { NotifyAppointmentPatientWhatsApp } = DataProviderInstance();
  const [feedback, setFeedback] = useState(null); // { message, severity }

  // onResult (opcional): quem chamou fica sabendo se saiu (ex.: registrar a oferta)
  const notifyPatient = useCallback((payload, onResult) => {
    if (!NotifyAppointmentPatientWhatsApp || !payload?.type) return;

    NotifyAppointmentPatientWhatsApp(payload, (result) => {
      onResult?.(result);
      if (result?.success) {
        const messages = SUCCESS_MESSAGES[payload.type] || SUCCESS_MESSAGES.created;
        setFeedback({
          severity: 'success',
          message: result.status === 'sent' ? messages.sent : messages.queued,
        });
        return;
      }
      setFeedback({ severity: 'error', message: result?.error || FAILURE_MESSAGE });
    });
  }, [NotifyAppointmentPatientWhatsApp]);

  const toast = (
    <Toast
      open={!!feedback}
      message={feedback?.message || ''}
      severity={feedback?.severity || 'success'}
      onClose={() => setFeedback(null)}
    />
  );

  return { canSendViaSystem, notifyPatient, toast };
}

export default useAgendaWhatsAppSend;
